import { observePwaRegistration } from "./pwaUpdate";

type WorkerLike = Pick<ServiceWorker, "scriptURL">;

type ServiceWorkerRegistrationLike = Pick<
  ServiceWorkerRegistration,
  "scope" | "unregister"
> & {
  active?: WorkerLike | null;
  installing?: WorkerLike | null;
  waiting?: WorkerLike | null;
  update?: () => Promise<unknown>;
};

type ServiceWorkerContainerLike = Pick<ServiceWorkerContainer, "register"> & {
  getRegistrations?: () => Promise<readonly ServiceWorkerRegistrationLike[]>;
};

interface RegisterServiceWorkerOptions {
  enabled?: boolean;
  origin?: string;
  publicUrl?: string;
  serviceWorker?: ServiceWorkerContainerLike;
}

const LEGACY_UNITY_SCOPE_PATH = "/unity/";
const LEGACY_UNITY_WORKER_PATH = "/unity/ServiceWorker.js";

export const getServiceWorkerUrl = (publicUrl = ""): string => {
  const normalized = publicUrl.trim().replace(/\/+$/, "");
  return `${normalized}/service-worker.js`;
};

export const unregisterLegacyUnityServiceWorker = async ({
  origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  serviceWorker =
    typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? navigator.serviceWorker
      : undefined,
}: Pick<
  RegisterServiceWorkerOptions,
  "origin" | "serviceWorker"
> = {}): Promise<number> => {
  if (!serviceWorker?.getRegistrations) return 0;

  const expectedScope = new URL(LEGACY_UNITY_SCOPE_PATH, origin).href;
  const expectedScriptUrl = new URL(LEGACY_UNITY_WORKER_PATH, origin).href;
  const registrations = await serviceWorker.getRegistrations();
  const targets = registrations.filter((registration) => {
    if (registration.scope !== expectedScope) return false;

    return [
      registration.active,
      registration.installing,
      registration.waiting,
    ].some((worker) => worker?.scriptURL === expectedScriptUrl);
  });

  const results = await Promise.all(
    targets.map((registration) => registration.unregister()),
  );
  return results.filter(Boolean).length;
};

export const registerServiceWorker = async ({
  enabled = process.env.NODE_ENV === "production",
  origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  publicUrl = process.env.PUBLIC_URL ?? "",
  serviceWorker =
    typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? navigator.serviceWorker
      : undefined,
}: RegisterServiceWorkerOptions = {}): Promise<ServiceWorkerRegistration | null> => {
  if (!enabled || !serviceWorker) return null;

  try {
    try {
      await unregisterLegacyUnityServiceWorker({ origin, serviceWorker });
    } catch (error) {
      console.warn(
        "Legacy Unity PWA cleanup failed. Continuing with React PWA setup.",
        error,
      );
    }
    const registration = await serviceWorker.register(
      getServiceWorkerUrl(publicUrl),
      {
        scope: `${publicUrl.trim().replace(/\/+$/, "") || ""}/`,
        updateViaCache: "none",
      },
    );
    observePwaRegistration(
      registration,
      serviceWorker as ServiceWorkerContainer,
    );
    try {
      await registration.update?.();
    } catch (error) {
      console.warn(
        "PWA update check failed. Continuing with the active version.",
        error,
      );
    }
    return registration;
  } catch (error) {
    console.warn("PWA setup failed. Reload the page and try again.", error);
    return null;
  }
};
